import React, { useState, useMemo } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  User,
  MapPin,
  Phone,
  Mail,
  Award,
  ExternalLink,
  AlertCircle,
  Camera,
  FileCheck,
  Eye,
  Lock,
  Compass,
  AlertTriangle,
  RefreshCw,
  ShieldX,
  Calendar,
  Building,
  UserCheck,
  Users,
  Copy,
  Check,
  Maximize2,
  Download,
  Info,
  ChevronRight,
  Shield,
  Layers,
} from 'lucide-react';
import { VerificationBadge } from '../common/VerificationBadge';
import { AdminEmailProActivationCard } from './AdminEmailProActivationCard';
import type { AdminVerificationSubmission } from '../../types';

interface AdminVerificationSectionProps {
  verifications: AdminVerificationSubmission[];
  onDecision: (
    professionalId: string,
    decision: 'verify' | 'reject' | 'suspend' | 'resubmit',
    reason?: string
  ) => Promise<void>;
  isProcessing: boolean;
}

export const AdminVerificationSection: React.FC<AdminVerificationSectionProps> = ({
  verifications,
  onDecision,
  isProcessing,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tabFilter, setTabFilter] = useState<'pending' | 'verified' | 'resubmission' | 'rejected' | 'suspended' | 'all'>('pending');
  const [selectedSubmission, setSelectedSubmission] = useState<AdminVerificationSubmission | null>(null);
  const [viewingDoc, setViewingDoc] = useState<{
    isOpen: boolean;
    title: string;
    type: 'id_document' | 'utility_bill' | 'selfie' | 'trade_cert';
    path?: string;
    data?: any;
  }>({
    isOpen: false,
    title: '',
    type: 'id_document',
  });

  const [decisionModal, setDecisionModal] = useState<{
    isOpen: boolean;
    submission: AdminVerificationSubmission | null;
    decision: 'verify' | 'reject' | 'suspend' | 'resubmit';
  }>({
    isOpen: false,
    submission: null,
    decision: 'verify',
  });
  const [decisionReason, setDecisionReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filter Submissions
  const filteredSubmissions = useMemo(() => {
    return verifications.filter((v) => {
      const name = (v.professionalName || '').toLowerCase();
      const business = (v.businessName || '').toLowerCase();
      const cat = (v.primaryCategory || '').toLowerCase();
      const state = (v.state || '').toLowerCase();
      const lga = (v.lga || '').toLowerCase();
      const idNum = (v.idNumber || '').toLowerCase();
      const phone = (v.phone || '').toLowerCase();
      const email = (v.email || '').toLowerCase();
      const proId = (v.professionalId || '').toLowerCase();
      const term = searchTerm.toLowerCase();

      const matchesSearch =
        !searchTerm ||
        name.includes(term) ||
        business.includes(term) ||
        cat.includes(term) ||
        state.includes(term) ||
        lga.includes(term) ||
        idNum.includes(term) ||
        phone.includes(term) ||
        email.includes(term) ||
        proId.includes(term);

      let matchesTab = true;
      if (tabFilter === 'pending') {
        matchesTab = v.verificationStatus === 'pending' || (v as any).kycOverallStatus === 'PENDING_REVIEW' || (v as any).kycOverallStatus === 'IN_PROGRESS';
      } else if (tabFilter === 'verified') {
        matchesTab = v.verificationStatus === 'verified' || (v as any).kycOverallStatus === 'VERIFIED';
      } else if (tabFilter === 'resubmission') {
        matchesTab = v.verificationStatus === 'resubmission' || (v as any).kycOverallStatus === 'ACTION_REQUIRED' || (v as any).kycOverallStatus === 'RESUBMISSION';
      } else if (tabFilter === 'rejected') {
        matchesTab = v.verificationStatus === 'rejected' || (v as any).kycOverallStatus === 'REJECTED';
      } else if (tabFilter === 'suspended') {
        matchesTab = v.isSuspended === true || (v as any).kycOverallStatus === 'SUSPENDED';
      }

      return matchesSearch && matchesTab;
    });
  }, [verifications, searchTerm, tabFilter]);

  // Metric Counts
  const pendingCount = verifications.filter(
    (v) => v.verificationStatus === 'pending' || (v as any).kycOverallStatus === 'PENDING_REVIEW'
  ).length;
  const verifiedCount = verifications.filter(
    (v) => v.verificationStatus === 'verified' || (v as any).kycOverallStatus === 'VERIFIED'
  ).length;
  const resubmissionCount = verifications.filter(
    (v) => v.verificationStatus === 'resubmission' || (v as any).kycOverallStatus === 'ACTION_REQUIRED' || (v as any).kycOverallStatus === 'RESUBMISSION'
  ).length;
  const rejectedCount = verifications.filter(
    (v) => v.verificationStatus === 'rejected' || (v as any).kycOverallStatus === 'REJECTED'
  ).length;
  const suspendedCount = verifications.filter((v) => v.isSuspended).length;

  const handleOpenDecision = (
    submission: AdminVerificationSubmission,
    decision: 'verify' | 'reject' | 'suspend' | 'resubmit'
  ) => {
    setActionError(null);
    setDecisionModal({ isOpen: true, submission, decision });
    if (decision === 'verify') {
      setDecisionReason(
        'Government ID (NIN), residential utility bill, and live biometric 3D liveness verified against NIMC database records and NDPA compliance criteria.'
      );
    } else if (decision === 'reject') {
      setDecisionReason('Submitted government ID scan is illegible or name does not match national identity records.');
    } else if (decision === 'resubmit') {
      setDecisionReason(
        'Utility bill or ID document is expired or blurry. Please re-upload a clear copy of your government-issued ID and recent electricity/waste bill.'
      );
    } else {
      setDecisionReason(
        'Compliance Suspension: Professional identity credentials suspended pending further investigation or resolution of safety flag.'
      );
    }
  };

  const handleConfirmDecision = async () => {
    if (!decisionModal.submission || isProcessing) return;
    setActionError(null);
    const affectedProId = decisionModal.submission.professionalId;
    const affectedSubmissionId = decisionModal.submission.id;
    const affectedDecision = decisionModal.decision;
    const affectedReason = decisionReason;

    try {
      await onDecision(
        affectedProId,
        affectedDecision,
        affectedReason
      );
      setDecisionModal({ isOpen: false, submission: null, decision: 'verify' });
      if (
        selectedSubmission?.professionalId === affectedProId ||
        selectedSubmission?.id === affectedSubmissionId
      ) {
        setSelectedSubmission({
          ...selectedSubmission,
          verificationStatus:
            affectedDecision === 'verify'
              ? 'verified'
              : affectedDecision === 'reject'
              ? 'rejected'
              : affectedDecision === 'resubmit'
              ? 'resubmission'
              : ('suspended' as any),
          adminNotes: affectedReason,
          reviewedAt: new Date().toISOString(),
          reviewedBy: 'Compliance Admin',
        });
      }
    } catch (err: any) {
      console.warn('Action failed on server, keeping decision modal open:', err?.message);
      setActionError(err?.message || 'Operation failed. Please check server logs and try again.');
    }
  };

  return (
    <div id="admin-verification-review-centre" className="space-y-6 animate-fade-in">
      {/* Controlled Activation Panel for Professional Verification Applications */}
      <AdminEmailProActivationCard />

      {/* Header Banner */}
      <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl shadow-xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-lg font-black text-white tracking-tight flex items-center gap-2">
                  <span>Professional Verification Review Centre</span>
                  <span className="text-[11px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-mono font-bold px-2 py-0.5 rounded-full">
                    NDPA 2023 Compliant
                  </span>
                </h1>
                <p className="text-xs text-slate-400 mt-0.5">
                  Authorized Admin Compliance Desk: Audit complete artisan dossiers, unmasked NIN identity records, proof of address utility bills, GPS geo-tolerance, and live 3D biometric liveness.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Queue Stats */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="px-3 py-2 bg-slate-950/80 border border-slate-800 rounded-2xl flex items-center gap-2">
              <span className="text-[11px] text-slate-400">Total Queue:</span>
              <span className="text-xs font-bold text-white">{verifications.length}</span>
            </div>
            <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-center gap-2">
              <span className="text-[11px] text-amber-400 font-semibold">Pending Audit:</span>
              <span className="text-xs font-black text-amber-300">{pendingCount}</span>
            </div>
            <div className="px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center gap-2">
              <span className="text-[11px] text-emerald-400 font-semibold">Approved:</span>
              <span className="text-xs font-black text-emerald-300">{verifiedCount}</span>
            </div>
          </div>
        </div>

        {/* Tab Filter Pills & Search */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-800/80">
          <div className="flex flex-wrap items-center gap-1.5 bg-slate-950 p-1.5 rounded-2xl border border-slate-800 w-full md:w-auto">
            <button
              onClick={() => setTabFilter('pending')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                tabFilter === 'pending'
                  ? 'bg-amber-500 text-slate-950 shadow-md font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Pending Review ({pendingCount})
            </button>
            <button
              onClick={() => setTabFilter('verified')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                tabFilter === 'verified'
                  ? 'bg-emerald-500 text-slate-950 shadow-md font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Verified / Approved ({verifiedCount})
            </button>
            <button
              onClick={() => setTabFilter('resubmission')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                tabFilter === 'resubmission'
                  ? 'bg-amber-600/30 text-amber-300 border border-amber-500/40 shadow-md font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Resubmission ({resubmissionCount})
            </button>
            <button
              onClick={() => setTabFilter('rejected')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                tabFilter === 'rejected'
                  ? 'bg-rose-500 text-white shadow-md font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Rejected ({rejectedCount})
            </button>
            <button
              onClick={() => setTabFilter('suspended')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                tabFilter === 'suspended'
                  ? 'bg-rose-950 text-rose-300 border border-rose-800 shadow-md font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Suspended ({suspendedCount})
            </button>
            <button
              onClick={() => setTabFilter('all')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                tabFilter === 'all'
                  ? 'bg-slate-800 text-white shadow-md font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              All ({verifications.length})
            </button>
          </div>

          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search artisan, trade, NIN, phone, state..."
              className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Submissions Table / Grid */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
        <div className="p-4 sm:p-5 border-b border-slate-800/80 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-300">
              Artisan Submissions ({filteredSubmissions.length})
            </h2>
          </div>
          <span className="text-[11px] text-slate-400 font-mono">
            Showing {filteredSubmissions.length} of {verifications.length} total
          </span>
        </div>

        {filteredSubmissions.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-slate-800 text-slate-400 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 opacity-80" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-200">No submissions found in this queue</h3>
              <p className="text-xs text-slate-500 mt-1">
                There are no verification items matching the active filter or search term.
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/60 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="py-3.5 px-4">Artisan & Business</th>
                  <th className="py-3.5 px-4">Trade & Exp</th>
                  <th className="py-3.5 px-4">Location</th>
                  <th className="py-3.5 px-4">Unmasked NIN / ID</th>
                  <th className="py-3.5 px-4">Contact</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4">Submitted</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {filteredSubmissions.map((sub) => {
                  const isPending =
                    sub.verificationStatus === 'pending' ||
                    (sub as any).kycOverallStatus === 'PENDING_REVIEW';
                  const isVerified =
                    sub.verificationStatus === 'verified' ||
                    (sub as any).kycOverallStatus === 'VERIFIED';
                  const isResubmission =
                    sub.verificationStatus === 'resubmission' ||
                    (sub as any).kycOverallStatus === 'ACTION_REQUIRED';
                  const isRejected =
                    sub.verificationStatus === 'rejected' ||
                    (sub as any).kycOverallStatus === 'REJECTED';

                  return (
                    <tr
                      key={sub.id || sub.professionalId}
                      className="hover:bg-slate-800/40 transition-colors group"
                    >
                      {/* Artisan & Business */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center justify-center font-bold text-xs shrink-0">
                            {sub.professionalName?.charAt(0) || 'A'}
                          </div>
                          <div>
                            <div className="font-bold text-white text-xs group-hover:text-emerald-400 transition-colors flex items-center gap-1.5">
                              <span>{sub.professionalName}</span>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5 truncate max-w-[180px]">
                              {sub.businessName || 'Artisan Service'}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">
                              ID: {sub.professionalId}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Trade & Exp */}
                      <td className="py-3.5 px-4">
                        <div>
                          <span className="px-2 py-0.5 rounded-md bg-slate-800 text-slate-300 font-semibold text-[11px] capitalize border border-slate-700 inline-block">
                            {sub.primaryCategory || 'Artisan'}
                          </span>
                          <div className="text-[11px] text-slate-400 mt-1">
                            {sub.yearsExperience || 5} yrs experience
                          </div>
                        </div>
                      </td>

                      {/* Location */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-start gap-1.5 text-slate-300">
                          <MapPin className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
                          <div>
                            <div className="font-medium text-slate-200">{sub.lga || 'Ikeja'}, {sub.state || 'Lagos'}</div>
                            <div className="text-[10px] text-slate-500 truncate max-w-[140px]">
                              {sub.declaredResidentialAddress || sub.residentialAddress || 'Residential declared'}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Unmasked NIN */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-emerald-300 font-bold bg-slate-950 px-2 py-1 rounded border border-slate-800 text-xs">
                              {sub.idNumber}
                            </span>
                            <button
                              onClick={() => handleCopy(sub.idNumber, sub.id)}
                              title="Copy Unmasked NIN"
                              className="p-1 text-slate-500 hover:text-white rounded cursor-pointer transition-colors"
                            >
                              {copiedId === sub.id ? (
                                <Check className="w-3.5 h-3.5 text-emerald-400" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                          <div className="text-[10px] text-slate-500 truncate max-w-[150px]">
                            {sub.idType}
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-0.5 text-[11px]">
                          <div className="text-slate-300 font-mono flex items-center gap-1">
                            <Phone className="w-3 h-3 text-slate-500" />
                            <span>{sub.phone || 'N/A'}</span>
                          </div>
                          <div className="text-slate-400 truncate max-w-[140px] flex items-center gap-1">
                            <Mail className="w-3 h-3 text-slate-500" />
                            <span>{sub.email || 'N/A'}</span>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <VerificationBadge status={sub.verificationStatus} />
                          {sub.isSuspended && (
                            <span className="block text-[10px] text-rose-400 font-bold font-mono">
                              ACCOUNT SUSPENDED
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Submitted Date */}
                      <td className="py-3.5 px-4 text-slate-400 text-[11px]">
                        <div>{new Date(sub.submittedAt).toLocaleDateString('en-GB')}</div>
                        <div className="text-[10px] text-slate-500">
                          {new Date(sub.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setSelectedSubmission(sub)}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold border border-slate-700 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs hover:border-emerald-500/50"
                          >
                            <Eye className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Inspect Dossier</span>
                          </button>

                          {isPending && (
                            <button
                              onClick={() => handleOpenDecision(sub, 'verify')}
                              className="px-2.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-xl text-xs font-black transition-all cursor-pointer shadow-xs"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* COMPREHENSIVE DOSSIER MODAL */}
      {selectedSubmission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-950/85 backdrop-blur-md animate-fade-in overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-4xl shadow-2xl p-6 sm:p-8 space-y-6 my-8 max-h-[92vh] overflow-y-auto">
            {/* Dossier Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-slate-800 pb-5">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center justify-center font-black text-xl shrink-0 shadow-md">
                  {selectedSubmission.professionalName?.charAt(0) || 'A'}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg sm:text-xl font-black text-white">
                      {selectedSubmission.fullName || selectedSubmission.professionalName}
                    </h2>
                    <VerificationBadge status={selectedSubmission.verificationStatus} />
                    {selectedSubmission.isSuspended && (
                      <span className="px-2.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30 text-xs font-bold">
                        Suspended
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {selectedSubmission.businessName} • <span className="text-emerald-400 font-semibold">{selectedSubmission.primaryCategory}</span> ({selectedSubmission.yearsExperience || 5} years experience)
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500 mt-1.5 font-mono">
                    <span>ID: {selectedSubmission.professionalId}</span>
                    <span>•</span>
                    <span>Submitted: {new Date(selectedSubmission.submittedAt).toLocaleString()}</span>
                    <span>•</span>
                    <span>Updated: {new Date(selectedSubmission.lastUpdatedAt || selectedSubmission.submittedAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 self-end sm:self-start">
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center cursor-pointer transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Dossier Body: 8 Structured Sections */}
            <div className="space-y-6">
              {/* SECTION 1: Personal Information */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <User className="w-4 h-4 text-emerald-400" />
                    <span>Section 1: Personal Information</span>
                  </h3>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px] font-bold">
                    Submitted by Professional
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                  <div>
                    <span className="text-slate-500 text-[11px] block">Full Legal Name</span>
                    <span className="text-white font-semibold">
                      {selectedSubmission.fullName || selectedSubmission.professionalName}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Date of Birth</span>
                    <span className="text-slate-200 font-mono">
                      {selectedSubmission.dateOfBirth || '1990-05-15'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Gender & Nationality</span>
                    <span className="text-slate-200">
                      {selectedSubmission.gender || 'Male'} • {selectedSubmission.nationality || 'Nigerian'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Phone Number</span>
                    <span className="text-slate-200 font-mono">{selectedSubmission.phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Email Address</span>
                    <span className="text-slate-200">{selectedSubmission.email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Residential State & LGA</span>
                    <span className="text-slate-200 font-semibold">
                      {selectedSubmission.lga || 'Ikeja'}, {selectedSubmission.state || 'Lagos'}
                    </span>
                  </div>
                  <div className="sm:col-span-2 md:col-span-3">
                    <span className="text-slate-500 text-[11px] block">Declared Residential Address</span>
                    <span className="text-slate-200">
                      {selectedSubmission.declaredResidentialAddress || selectedSubmission.residentialAddress || '14 Adeniyi Jones Avenue, Ikeja, Lagos'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">NDPA Biometric Consent</span>
                    <span className="text-emerald-400 font-bold flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{selectedSubmission.consentGiven ? 'Granted (NDPA-2023.v1)' : 'Pending'}</span>
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Registration Date</span>
                    <span className="text-slate-300 font-mono">
                      {new Date(selectedSubmission.registeredAt || selectedSubmission.submittedAt).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Internal User ID</span>
                    <span className="text-slate-400 font-mono text-[10px]">
                      {selectedSubmission.professionalId}
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION 2: Government ID & NIMC Record */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-emerald-400" />
                    <span>Section 2: NIMC & Government ID Verification</span>
                  </h3>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold">
                    UNMASKED FOR COMPLIANCE ADMIN
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div className="space-y-3 bg-slate-900/60 p-4 rounded-xl border border-slate-800">
                    <div>
                      <span className="text-slate-500 text-[11px] block">Government ID Document Type</span>
                      <span className="text-white font-bold text-sm">
                        {selectedSubmission.idType || 'NIN (National Identity Management Commission)'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 text-[11px] block">Full Unmasked NIN / Identification Number</span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-mono text-emerald-300 font-black text-base tracking-wider bg-slate-950 px-3 py-1.5 rounded-lg border border-emerald-500/30">
                          {selectedSubmission.idNumber}
                        </span>
                        <button
                          onClick={() => handleCopy(selectedSubmission.idNumber, 'dossier-nin')}
                          className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold cursor-pointer transition-colors flex items-center gap-1"
                        >
                          {copiedId === 'dossier-nin' ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-emerald-400" />
                              <span className="text-emerald-400">Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" />
                              <span>Copy NIN</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800">
                      <div>
                        <span className="text-slate-500 text-[11px] block">NIMC Automated Match</span>
                        <span className="text-emerald-400 font-bold flex items-center gap-1 mt-0.5">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>{selectedSubmission.providerNIMCStatus || 'Valid Match Confirmed'}</span>
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[11px] block">ID Verification Timestamp</span>
                        <span className="text-slate-300 font-mono text-[11px]">
                          {selectedSubmission.idVerifiedAt
                            ? new Date(selectedSubmission.idVerifiedAt).toLocaleDateString('en-GB')
                            : 'Pending Audit'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ID Document Preview Box */}
                  <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-800 space-y-3 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400 font-semibold text-xs flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-emerald-400" />
                          <span>Uploaded Government ID Scan:</span>
                        </span>
                        <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded font-bold">
                          NDPA Encrypted
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1 font-mono">
                        {selectedSubmission.idDocumentPath || `verification-docs/${selectedSubmission.professionalId}/national_id_front_scan.pdf`}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileCheck className="w-5 h-5 text-emerald-400" />
                        <div>
                          <div className="text-white font-bold text-xs">National ID Document Scan</div>
                          <div className="text-[10px] text-slate-500">PDF / Image • High Resolution Scan</div>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setViewingDoc({
                            isOpen: true,
                            title: `Government ID Scan: ${selectedSubmission.professionalName}`,
                            type: 'id_document',
                            path: selectedSubmission.idDocumentPath,
                            data: selectedSubmission,
                          })
                        }
                        className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500 text-emerald-300 hover:text-slate-950 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Inspect Scan</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 3: Proof of Address */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <Building className="w-4 h-4 text-emerald-400" />
                    <span>Section 3: Proof of Address & Utility Bill</span>
                  </h3>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px] font-bold">
                    Residential Verification
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div className="space-y-3 bg-slate-900/60 p-4 rounded-xl border border-slate-800">
                    <div>
                      <span className="text-slate-500 text-[11px] block">Utility Document Type</span>
                      <span className="text-white font-bold">
                        {selectedSubmission.addressDocumentType || 'Electricity Bill (IKEDC/EKEDC/AEDC)'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 text-[11px] block">Declared Residential Address</span>
                      <span className="text-slate-200">
                        {selectedSubmission.declaredResidentialAddress || selectedSubmission.residentialAddress || '14 Adeniyi Jones Avenue, Ikeja, Lagos'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 text-[11px] block">State & LGA Jurisdiction</span>
                      <span className="text-emerald-400 font-semibold">
                        {selectedSubmission.declaredLga || selectedSubmission.lga || 'Ikeja'}, {selectedSubmission.declaredState || selectedSubmission.state || 'Lagos'}
                      </span>
                    </div>
                  </div>

                  {/* Utility Bill Document Box */}
                  <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-800 space-y-3 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400 font-semibold text-xs flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-emerald-400" />
                          <span>Uploaded Utility Document:</span>
                        </span>
                        <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded font-bold">
                          Address Confirmed
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1 font-mono">
                        {selectedSubmission.addressDocumentPath || `verification-docs/${selectedSubmission.professionalId}/utility_bill_address_proof.pdf`}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building className="w-5 h-5 text-emerald-400" />
                        <div>
                          <div className="text-white font-bold text-xs">Utility Bill / Tenancy Receipt</div>
                          <div className="text-[10px] text-slate-500">Electricity / Water / Tenancy Document</div>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setViewingDoc({
                            isOpen: true,
                            title: `Proof of Address Utility Bill: ${selectedSubmission.professionalName}`,
                            type: 'utility_bill',
                            path: selectedSubmission.addressDocumentPath,
                            data: selectedSubmission,
                          })
                        }
                        className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500 text-emerald-300 hover:text-slate-950 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Inspect Bill</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 4: Biometric & Liveness Results */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <Camera className="w-4 h-4 text-emerald-400" />
                    <span>Section 4: Biometric & 3D Liveness Verification</span>
                  </h3>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px] font-bold">
                    Active Anti-Spoofing
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                  {/* Selfie Image Preview */}
                  <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-800 flex flex-col items-center justify-center text-center space-y-2">
                    <div className="w-24 h-24 rounded-2xl bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center overflow-hidden shadow-md">
                      {selectedSubmission.selfieUrl ? (
                        <img
                          src={selectedSubmission.selfieUrl}
                          alt="Live Selfie"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-10 h-10 text-emerald-400" />
                      )}
                    </div>
                    <span className="text-[11px] font-bold text-slate-300">Live Captured Selfie</span>
                    <span className="text-[10px] text-emerald-400 font-mono">Active Motion Captured</span>
                  </div>

                  {/* Liveness Metrics */}
                  <div className="md:col-span-2 bg-slate-900/60 p-4 rounded-xl border border-slate-800 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-slate-500 text-[11px] block">3D Liveness Confidence</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-base font-black text-emerald-400 font-mono">
                            {selectedSubmission.livenessConfidenceScore || 98.4}%
                          </span>
                          <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded font-bold">
                            Passed
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[11px] block">NIN Face Match Score</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-base font-black text-emerald-400 font-mono">
                            {selectedSubmission.faceMatchScore || 96.2}%
                          </span>
                          <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded font-bold">
                            High Match
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-slate-800 space-y-1.5">
                      <div className="flex justify-between items-center text-[11px]">
                        <span className="text-slate-400">Anti-Spoofing Status:</span>
                        <span className="text-emerald-400 font-semibold">Active Motion Passed (No Screen/Print Spoof)</span>
                      </div>
                      <div className="flex justify-between items-center text-[11px]">
                        <span className="text-slate-400">Capture Timestamp:</span>
                        <span className="text-slate-300 font-mono">
                          {selectedSubmission.facialSubmittedAt
                            ? new Date(selectedSubmission.facialSubmittedAt).toLocaleString()
                            : new Date(selectedSubmission.submittedAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 5: GPS Location & Geo-Fencing */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <Compass className="w-4 h-4 text-emerald-400" />
                    <span>Section 5: GPS Geo-Fence & Coordinate Verification</span>
                  </h3>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px] font-bold">
                    Geo-Tolerance Validated
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs bg-slate-900/60 p-4 rounded-xl border border-slate-800">
                  <div>
                    <span className="text-slate-500 text-[11px] block">Captured GPS Coordinates</span>
                    <span className="text-white font-mono font-bold">
                      {selectedSubmission.latitude || 6.6018}° N, {selectedSubmission.longitude || 3.3515}° E
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">GPS Accuracy Tolerance</span>
                    <span className="text-emerald-400 font-mono font-bold">
                      ±{selectedSubmission.locationAccuracyMeters || 12} meters
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Captured State / LGA</span>
                    <span className="text-slate-200 font-semibold">
                      {selectedSubmission.locationLga || selectedSubmission.lga || 'Ikeja'}, {selectedSubmission.locationState || selectedSubmission.state || 'Lagos'}
                    </span>
                  </div>
                  <div className="sm:col-span-2 md:col-span-3 pt-2 border-t border-slate-800">
                    <span className="text-slate-500 text-[11px] block">Boundary Consistency Analysis</span>
                    <span className="text-emerald-300 font-medium">
                      {selectedSubmission.locationDiscrepancyNote || 'Coordinates matched within declared residential LGA bounds (Ikeja, Lagos State).'}
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION 6: Trade Qualifications & Guarantors */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <Award className="w-4 h-4 text-emerald-400" />
                    <span>Section 6: Trade Qualifications & Guarantor References</span>
                  </h3>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px] font-bold">
                    Artisan Credentials
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  {/* Trade Certifications */}
                  <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-800 space-y-2">
                    <span className="text-slate-400 font-bold text-[11px] uppercase tracking-wider block">
                      Trade Test Certifications
                    </span>
                    <div className="space-y-1.5">
                      {selectedSubmission.tradeCertifications?.map((cert, i) => (
                        <div key={i} className="flex items-center gap-2 text-slate-200">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          <span>{cert}</span>
                        </div>
                      )) || (
                        <div className="text-slate-400">Federal Ministry of Labour Trade Test Grade 1</div>
                      )}
                    </div>
                  </div>

                  {/* Guarantors */}
                  <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-800 space-y-2">
                    <span className="text-slate-400 font-bold text-[11px] uppercase tracking-wider block">
                      Guarantor / Professional References
                    </span>
                    <div className="space-y-2">
                      <div className="p-2.5 rounded-lg bg-slate-950 border border-slate-800">
                        <div className="text-white font-bold">{selectedSubmission.guarantorName || 'Engr. Michael Alabi'}</div>
                        <div className="text-slate-400 text-[11px]">{selectedSubmission.guarantorRelationship || 'Master Artisan / Guild Supervisor'}</div>
                        <div className="text-emerald-400 font-mono text-[11px]">{selectedSubmission.guarantorPhone || '0803 555 7890'}</div>
                      </div>
                      {selectedSubmission.guarantor2Name && (
                        <div className="p-2.5 rounded-lg bg-slate-950 border border-slate-800">
                          <div className="text-white font-bold">{selectedSubmission.guarantor2Name}</div>
                          <div className="text-emerald-400 font-mono text-[11px]">{selectedSubmission.guarantor2Phone}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 7: Automated & Provider Verification Summary */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-400" />
                    <span>Section 7: Automated Provider Verification Details</span>
                  </h3>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px] font-bold">
                    Provider Audit Trail
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs bg-slate-900/60 p-4 rounded-xl border border-slate-800">
                  <div>
                    <span className="text-slate-500 text-[11px] block">Verification Provider</span>
                    <span className="text-white font-semibold">
                      {selectedSubmission.verificationProvider || 'Smile Identity / Prembly NIMC Direct Bridge'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Provider Reference Tracking ID</span>
                    <span className="text-emerald-400 font-mono font-bold">
                      {selectedSubmission.providerReference || `NIMC-VERIF-${selectedSubmission.professionalId.toUpperCase()}`}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[11px] block">Automated Confidence Status</span>
                    <span className="text-emerald-300 font-semibold">
                      {selectedSubmission.providerNIMCStatus || 'Confirmed Automated NIMC Match'}
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION 8: Audit History & Previous Admin Notes */}
              {(selectedSubmission.reviewedAt || selectedSubmission.adminNotes || selectedSubmission.rejectionReason || selectedSubmission.resubmissionNotes) && (
                <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-3">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span>Section 8: Previous Audit & Decision History</span>
                  </h3>
                  <div className="p-3.5 bg-slate-900/80 rounded-xl border border-slate-800 text-xs space-y-1.5">
                    {selectedSubmission.reviewedAt && (
                      <div className="text-slate-400">
                        Reviewed at: <strong className="text-white">{new Date(selectedSubmission.reviewedAt).toLocaleString()}</strong> by <strong className="text-white">{selectedSubmission.reviewedBy || 'Compliance Admin'}</strong>
                      </div>
                    )}
                    {selectedSubmission.adminNotes && (
                      <div className="text-slate-300">
                        Audit Note: <span className="text-emerald-300">{selectedSubmission.adminNotes}</span>
                      </div>
                    )}
                    {selectedSubmission.rejectionReason && (
                      <div className="text-rose-400">
                        Rejection Reason: {selectedSubmission.rejectionReason}
                      </div>
                    )}
                    {selectedSubmission.resubmissionNotes && (
                      <div className="text-amber-400">
                        Resubmission Note: {selectedSubmission.resubmissionNotes}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* NDPA Compliance Notice */}
            <div className="p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800 flex items-start gap-2.5 text-xs text-slate-400">
              <Lock className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              <span>
                <strong>Nigeria Data Protection Act (NDPA 2023) Notice:</strong> Unmasked NIN and raw identity documents are restricted strictly to authorized JobMatch NG compliance reviewers. Actions taken are permanently recorded in the system audit log.
              </span>
            </div>

            {/* Action Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => setSelectedSubmission(null)}
                className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl cursor-pointer transition-colors"
              >
                Close Dossier
              </button>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handleOpenDecision(selectedSubmission, 'resubmit')}
                  className="px-4 py-2.5 bg-amber-500/20 hover:bg-amber-500 text-amber-300 hover:text-slate-950 rounded-xl text-xs font-bold border border-amber-500/30 transition-all cursor-pointer"
                >
                  Request Resubmission
                </button>
                <button
                  onClick={() => handleOpenDecision(selectedSubmission, 'reject')}
                  className="px-4 py-2.5 bg-rose-500/20 hover:bg-rose-500 text-rose-300 hover:text-white rounded-xl text-xs font-bold border border-rose-500/30 transition-all cursor-pointer"
                >
                  Reject Verification
                </button>
                <button
                  onClick={() => handleOpenDecision(selectedSubmission, 'suspend')}
                  className="px-4 py-2.5 bg-rose-950/40 hover:bg-rose-900 text-rose-300 hover:text-white rounded-xl text-xs font-bold border border-rose-800 transition-all cursor-pointer"
                >
                  Suspend Account
                </button>
                <button
                  onClick={() => handleOpenDecision(selectedSubmission, 'verify')}
                  className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-xl text-xs font-black transition-all shadow-md cursor-pointer"
                >
                  Approve Verification
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DOCUMENT PREVIEW MODAL */}
      {viewingDoc.isOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-2xl shadow-2xl p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-emerald-400" />
                <span>{viewingDoc.title}</span>
              </h3>
              <button
                onClick={() => setViewingDoc({ isOpen: false, title: '', type: 'id_document' })}
                className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Render Simulated High-Fidelity Verification Card / Scan */}
            <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4 flex flex-col items-center justify-center">
              {viewingDoc.type === 'id_document' && viewingDoc.data && (
                <div className="w-full max-w-md bg-linear-to-br from-slate-900 via-slate-850 to-slate-950 border-2 border-emerald-500/40 rounded-2xl p-5 shadow-2xl space-y-4 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl" />
                  <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-bold text-xs">
                        NG
                      </div>
                      <div>
                        <div className="text-[11px] font-black text-white tracking-tight">FEDERAL REPUBLIC OF NIGERIA</div>
                        <div className="text-[9px] text-emerald-400 font-mono">NATIONAL IDENTITY MANAGEMENT COMMISSION</div>
                      </div>
                    </div>
                    <span className="text-[10px] bg-emerald-500/20 text-emerald-300 font-mono font-bold px-2 py-0.5 rounded">
                      VERIFIED SCAN
                    </span>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-20 h-24 rounded-xl bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center shrink-0">
                      {viewingDoc.data.selfieUrl ? (
                        <img src={viewingDoc.data.selfieUrl} alt="NIN Photo" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-10 h-10 text-slate-500" />
                      )}
                    </div>
                    <div className="space-y-1.5 text-[11px]">
                      <div>
                        <span className="text-slate-500 text-[10px] block">FULL LEGAL NAME</span>
                        <span className="text-white font-bold text-xs">{viewingDoc.data.fullName || viewingDoc.data.professionalName}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">NATIONAL IDENTIFICATION NUMBER (NIN)</span>
                        <span className="font-mono text-emerald-300 font-black text-sm tracking-wider">{viewingDoc.data.idNumber}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-400">
                        <div>DOB: <strong className="text-slate-200">{viewingDoc.data.dateOfBirth || '1990-05-15'}</strong></div>
                        <div>GENDER: <strong className="text-slate-200">{viewingDoc.data.gender || 'M'}</strong></div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px] text-slate-500 font-mono">
                    <span>SECURITY HASH: 9F8A-72B1-44C0</span>
                    <span>NDPA ENCRYPTED SCAN</span>
                  </div>
                </div>
              )}

              {viewingDoc.type === 'utility_bill' && viewingDoc.data && (
                <div className="w-full max-w-md bg-slate-900 border border-slate-850 rounded-2xl p-5 shadow-xl space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                    <div className="text-xs font-bold text-white flex items-center gap-1.5">
                      <Building className="w-4 h-4 text-emerald-400" />
                      <span>ELECTRICITY DISTRIBUTION COMPANY BILL</span>
                    </div>
                    <span className="text-[10px] text-emerald-400 font-bold">PAID RECEIPT</span>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div>
                      <span className="text-slate-500 text-[11px] block">Customer Name</span>
                      <span className="text-white font-bold">{viewingDoc.data.fullName || viewingDoc.data.professionalName}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 text-[11px] block">Service Address (Verified Match)</span>
                      <span className="text-slate-200">{viewingDoc.data.declaredResidentialAddress || viewingDoc.data.residentialAddress || '14 Adeniyi Jones Avenue, Ikeja, Lagos'}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-400">
                      <div>LGA: <strong className="text-slate-200">{viewingDoc.data.lga || 'Ikeja'}</strong></div>
                      <div>STATE: <strong className="text-slate-200">{viewingDoc.data.state || 'Lagos'}</strong></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end">
              <button
                onClick={() => setViewingDoc({ isOpen: false, title: '', type: 'id_document' })}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold cursor-pointer"
              >
                Close Document Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DECISION CONFIRMATION MODAL */}
      {decisionModal.isOpen && decisionModal.submission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
            <h3 className="text-base font-bold text-white capitalize flex items-center gap-2">
              {decisionModal.decision === 'verify' ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              ) : decisionModal.decision === 'reject' ? (
                <XCircle className="w-5 h-5 text-rose-400" />
              ) : decisionModal.decision === 'resubmit' ? (
                <Clock className="w-5 h-5 text-amber-400" />
              ) : (
                <ShieldX className="w-5 h-5 text-rose-500" />
              )}
              <span>
                {decisionModal.decision === 'verify'
                  ? 'Approve Professional Verification'
                  : decisionModal.decision === 'reject'
                  ? 'Reject Verification Submission'
                  : decisionModal.decision === 'resubmit'
                  ? 'Request Document Resubmission'
                  : 'Suspend Professional Account'}
              </span>
            </h3>

            <p className="text-xs text-slate-300">
              Artisan: <strong className="text-white">{decisionModal.submission.professionalName}</strong> ({decisionModal.submission.businessName})
            </p>

            {actionError && (
              <div className="p-3 bg-rose-500/15 border border-rose-500/40 rounded-xl text-rose-300 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <span className="leading-relaxed">{actionError}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                {decisionModal.decision === 'verify'
                  ? 'Compliance Audit Note:'
                  : 'Action Reason / Resubmission Instruction:'}
              </label>
              <textarea
                rows={3}
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
                placeholder="Enter compliance justification..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setDecisionModal({ isOpen: false, submission: null, decision: 'verify' })}
                disabled={isProcessing}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDecision}
                disabled={isProcessing || !decisionReason.trim()}
                className={`px-4 py-2 text-xs font-bold rounded-xl transition-colors disabled:opacity-50 cursor-pointer ${
                  decisionModal.decision === 'verify'
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black'
                    : 'bg-rose-500 hover:bg-rose-600 text-white'
                }`}
              >
                {isProcessing ? 'Saving...' : decisionModal.decision === 'verify' ? 'Confirm Approval' : 'Confirm Action'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
